import { type ReactNode, useMemo } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { PipelineStageValueLabel } from '@/lib/crm/components/properties/labels/PipelineStageValueLabel';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import { PERSON_TYPES } from '@/lib/crm/core/entityTypes';
import type { EntityTypeName } from '@/lib/crm/core/types';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject, PropertyValue } from '@/types/crm';

import type { MergeCardRow } from './getMergeCardPropertyKeys';
import { getMergeCardRows } from './getMergeCardPropertyKeys';
import { isMergeValueEmpty } from './useMergePropertyResolution';

/** Max association targets rendered inline before collapsing to "+N". */
export const MAX_ASSOC_TARGETS_SHOWN = 2;

export interface CardLeadingAvatarProps {
  entityTypeName: EntityTypeName;
  name: string;
}

/**
 * Leading element for a merge card header. Person types (contact, user) get
 * an initials avatar — the two candidates are different people, so initials
 * differentiate them at a glance. Non-person types (deal, company, custom)
 * render NO leading element: both merge candidates are always the same
 * entity type, so a type icon is identical on both cards and only costs
 * horizontal space that long deal/company names need.
 */
export function CardLeadingAvatar({
  entityTypeName,
  name,
}: CardLeadingAvatarProps) {
  if (PERSON_TYPES.has(entityTypeName)) {
    return <Avatar size='lg' initials={initialsFor(name)} />;
  }
  return null;
}

export interface CardPropertyRowProps {
  obj: CrmObject | undefined;
  propertyKey: string;
  schema: PropertySchemaMap;
}

export function CardPropertyRow({
  obj,
  propertyKey,
  schema,
}: CardPropertyRowProps) {
  const raw: PropertyValue | undefined = obj?.properties?.[propertyKey];
  if (!raw || isMergeValueEmpty(raw.value)) {
    return null;
  }
  return (
    <div className='flex items-center gap-2 text-body-small text-[var(--color-text-secondary)]'>
      <span className='text-[var(--color-text-tertiary)]'>
        {schema[propertyKey]?.label ?? propertyKey}
      </span>
      <PropertyValueLabel
        value={raw.value}
        propertyType={schema[propertyKey]}
        fontSize='sm'
      />
    </div>
  );
}

export interface CardAssociationRowProps {
  row: Extract<MergeCardRow, { type: 'association' }>;
  label: string;
  isStage: boolean;
  orgId: number;
}

export function CardAssociationRow({
  row,
  label,
  isStage,
  orgId,
}: CardAssociationRowProps) {
  const shown = row.targetIds.slice(0, MAX_ASSOC_TARGETS_SHOWN);
  const overflow = row.targetIds.length - shown.length;
  return (
    <div className='flex items-center gap-2 text-body-small text-[var(--color-text-secondary)]'>
      <span className='text-[var(--color-text-tertiary)]'>{label}</span>
      {/* interactive={false} on both branches: this renders inside the merge
          modal — a badge click navigating away would abandon the in-progress
          merge flow. */}
      {isStage ? (
        <PipelineStageValueLabel
          value={row.targetIds[0]}
          fontSize='sm'
          interactive={false}
        />
      ) : (
        <span className='flex items-center gap-1'>
          {shown.map(id => (
            <EntityBadgeById
              key={id}
              entityId={id}
              orgId={orgId}
              variant='xs'
              interactive={false}
            />
          ))}
          {overflow > 0 && (
            <span className='text-[var(--color-text-tertiary)]'>
              +{overflow}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

export interface CardBodyProps {
  obj: CrmObject | undefined;
  name: string;
  schema: PropertySchemaMap;
  entityTypeName: EntityTypeName;
  orgId: number;
  assocAccessMap: ReadonlyMap<string, string>;
  assocLabelMap: ReadonlyMap<string, string>;
  /** Association type names whose target kind is `stage` (same key space as row.key). */
  stageAssocTypeNames: ReadonlySet<string>;
  headerSuffix?: ReactNode;
}

export function CardBody({
  obj,
  name,
  schema,
  entityTypeName,
  orgId,
  assocAccessMap,
  assocLabelMap,
  stageAssocTypeNames,
  headerSuffix,
}: CardBodyProps) {
  const rows = useMemo(
    () =>
      obj ? getMergeCardRows(entityTypeName, schema, obj, assocAccessMap) : [],
    [entityTypeName, schema, obj, assocAccessMap]
  );
  return (
    <>
      <div className='flex items-start justify-between gap-3'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          <CardLeadingAvatar entityTypeName={entityTypeName} name={name} />
          {/* min-w-0 lets the name shrink inside the flex row (otherwise an
              unbroken token overflows the card); line-clamp-2 bounds very
              long deal/company names so one card can't balloon the shared
              grid row; title= exposes the full name on hover. */}
          <span
            className='line-clamp-2 min-w-0 break-words text-heading-4 font-semibold text-[var(--color-text-primary)]'
            title={name}
          >
            {name}
          </span>
        </div>
        {headerSuffix}
      </div>
      <div className='flex flex-col gap-1'>
        {rows.map(row =>
          row.type === 'property' ? (
            <CardPropertyRow
              key={`p-${row.key}`}
              obj={obj}
              propertyKey={row.key}
              schema={schema}
            />
          ) : (
            <CardAssociationRow
              key={`a-${row.key}`}
              row={row}
              label={assocLabelMap.get(row.key) ?? row.key}
              isStage={stageAssocTypeNames.has(row.key)}
              orgId={orgId}
            />
          )
        )}
      </div>
      {!obj && (
        <div className='text-body-small text-[var(--color-text-tertiary)]'>
          Record unavailable
        </div>
      )}
    </>
  );
}

function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0] ?? '')
    .join('')
    .toUpperCase();
}
